import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api-client';
import { isApiRequestError } from '../../lib/api-error';

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ CROSS-MODULE PORT — article / supplier / unit lookup (web side)          ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * The receiving screens need three pickers that M1 master-data owns:
 * articles, suppliers and units. Receiving never writes them — it only needs to
 * search them for the "new purchase order" and "teach barcode" forms (the
 * scan-driven paths resolve articles from the barcode instead, which is the
 * whole point of §5.7).
 *
 * These call M1's list endpoints using the shared list-query contract. Until M1
 * ships them the requests 404, and the pickers degrade to a clear "register not
 * available yet" message instead of an unexplained empty dropdown or a crash.
 *
 * When M1 lands: nothing here changes. If its paths differ from the ones below,
 * this is the ONE file to correct.
 */

export interface ArticleOption {
  id: string;
  articleNo: string;
  name: string;
  baseUnitId: string;
  baseUnitCode: string;
  supplierId: string | null;
  lotTracked: boolean;
  expiryRequired: boolean;
}

export interface SupplierOption {
  id: string;
  name: string;
  code: string | null;
}

export interface UnitOption {
  id: string;
  code: string;
  name: string;
}

interface ListLike<T> {
  items: T[];
}

/** M1 endpoints as documented in MODULES.md §M1. */
const PATHS = {
  articles: '/articles',
  suppliers: '/suppliers',
  units: '/units',
} as const;

export interface LookupResult<T> {
  options: T[];
  isLoading: boolean;
  /** True when the register endpoint is not deployed yet (404 / 403). */
  unavailable: boolean;
}

function useLookup<T>(key: string, path: string, q: string, enabled = true): LookupResult<T> {
  const query = useQuery({
    queryKey: ['master-data', key, q],
    enabled,
    // Registers change rarely; re-querying them on every keystroke of a picker
    // that is already open is pure noise on a floor AP.
    staleTime: 60_000,
    retry: false,
    queryFn: () =>
      api.get<ListLike<T>>(path, {
        query: { ...(q ? { q } : {}), pageSize: 50, sort: 'name' },
      }),
  });

  const unavailable =
    query.isError &&
    isApiRequestError(query.error) &&
    (query.error.status === 404 || query.error.status === 403);

  return {
    options: query.data?.items ?? [],
    isLoading: query.isLoading,
    unavailable,
  };
}

export function useArticleOptions(q: string, enabled = true): LookupResult<ArticleOption> {
  return useLookup<ArticleOption>('articles', PATHS.articles, q, enabled);
}

export function useSupplierOptions(q = '', enabled = true): LookupResult<SupplierOption> {
  return useLookup<SupplierOption>('suppliers', PATHS.suppliers, q, enabled);
}

export function useUnitOptions(enabled = true): LookupResult<UnitOption> {
  return useLookup<UnitOption>('units', PATHS.units, '', enabled);
}
